import JSZip from "jszip";
import { saveAs } from "file-saver";
import * as XLSX from "xlsx";

import { buildWorkbookPayments } from "../sectionsIndicatorsCandidatures/exporters/exporterIndicatorPayments";
import { buildWorkbookDelegation } from "../sectionsIndicatorsCandidatures/exporters/exporterIndicatorDelegation";
import { buildAdministrativeDivisionAll } from "../sectionsIndicatorsCandidatures/exporters/exporterAdministrativeDivision";
import { buildWorkbookCae } from "../sectionsIndicatorsCandidatures/exporters/exporterIndicatorCAE";
import { buildWorkbookStatus } from "../sectionsIndicatorsCandidatures/exporters/exporterIndicatorStatus";
import { buildWorkbookRepresentative } from "../sectionsIndicatorsCandidatures/exporters/exporterIndicatorRepresentative";
import { buildWorkbookApplicationChanges } from "../sectionsIndicatorsCandidatures/exporters/exporterApplicationChanges";
import { buildWorkbookNonCompliance } from "../sectionsIndicatorsCandidatures/exporters/exporterIndicatorNonCompliance";

const PDF_ENDPOINTS = [
  {
    indicatorType: "indicadoresDelegacao",
    filename: "indicadores_delegacao.pdf",
  },
  { indicatorType: "indicadoresCAE", filename: "indicadores_cae.pdf" },
  { indicatorType: "indicadores", filename: "indicadores_estado.pdf" },
  {
    indicatorType: "indicadoresRepresentativos",
    filename: "indicadores_dados_representante.pdf",
  },
  {
    indicatorType: "indicadoresDivisaoAdministrativa",
    filename: "indicadores_divisao_administrativa.pdf",
  },
  {
    indicatorType: "indicadoresAlteracoesAplicacao",
    filename: "indicadores_alteracoes_candidatura.pdf",
  },
  {
    indicatorType: "incumprimentos",
    filename: "indicadores_incumprimentos.pdf",
  },
  { indicatorType: "pagamentos", filename: "indicadores_pagamentos.pdf" },
];

async function fetchPdfBlob(indicatorType) {
  const url = `/api/indicator/asPdf?indicatorType=${encodeURIComponent(
    indicatorType
  )}&t=${Date.now()}`;

  const resp = await fetch(url, {
    method: "GET",
    credentials: "include",
    cache: "no-store",
    headers: { Accept: "application/pdf" },
  });

  if (!resp.ok) throw new Error(`[PDF ${indicatorType}] HTTP ${resp.status}`);

  const ct = resp.headers.get("content-type") || "";
  const blob = await resp.blob();

  const head = await blob
    .slice(0, 5)
    .text()
    .catch(() => "");
  const looksPdf = (ct.includes("pdf") || head === "%PDF-") && blob.size > 0;

  if (!looksPdf) {
    let preview = "";
    try {
      preview = (await blob.text()).slice(0, 300);
    } catch (_) {}
    throw new Error(
      `[PDF ${indicatorType}] resposta não-PDF (${ct}). ${preview}`
    );
  }

  return blob;
}

function makeUniqueNamer() {
  const used = new Set();
  return (name) => {
    let base = String(name || "file").trim();
    if (!base) base = "file";
    let out = base;
    let n = 2;
    while (used.has(out)) {
      const dot = out.lastIndexOf(".");
      const ext = dot > -1 ? out.slice(dot) : "";
      const stem = dot > -1 ? out.slice(0, dot) : out;
      out = `${stem} (${n})${ext}`;
      n++;
    }
    used.add(out);
    return out;
  };
}

export async function exportAllPdfsZip(
  zipName = "indicadores_projetos_em_curso_pdfs.zip",
  progress
) {
  const zip = new JSZip();
  const uniq = makeUniqueNamer();
  let added = 0;

  for (const entry of PDF_ENDPOINTS || []) {
    if (!entry) continue;
    const { indicatorType, filename } = entry;
    if (!indicatorType || !filename) continue;

    try {
      const blob = await fetchPdfBlob(indicatorType);
      zip.file(uniq(filename), blob, { compression: "STORE" });
      added++;
    } catch (e) {
      zip.file(
        `__falhas/${indicatorType}.txt`,
        `Falha a obter PDF "${indicatorType}": ${e?.message || e}`
      );
    }
  }

  if (added === 0) {
    zip.file(
      "__info.txt",
      "Nenhum PDF adicionado. Verifique os indicatorType/sessão."
    );
  }

  const out = await zip.generateAsync(
    { type: "blob", compression: "STORE" },
    (metadata) => {
      progress?.onZipProgress?.(metadata.percent || 0);
    }
  );
  saveAs(out, zipName);
}

const EXCEL_EXPORTERS = [
  {
    id: "delegacao",
    filename: "indicadores_delegacao.xlsx",
    buildWorkbook: buildWorkbookDelegation,
  },
  {
    id: "cae",
    filename: "indicadores_cae.xlsx",
    buildWorkbook: buildWorkbookCae,
  },
  {
    id: "estado",
    filename: "indicadores_estado.xlsx",
    buildWorkbook: buildWorkbookStatus,
  },
  {
    id: "dados",
    filename: "indicadores_representativos.xlsx",
    buildWorkbook: buildWorkbookRepresentative,
  },
  { id: "divisao", buildMany: buildAdministrativeDivisionAll },
  {
    id: "alteracoes",
    filename: "indicadores_alteracoes_candidatura.xlsx",
    buildWorkbook: buildWorkbookApplicationChanges,
  },
  {
    id: "incumprimentos",
    filename: "indicadores_incumprimentos.xlsx",
    buildWorkbook: buildWorkbookNonCompliance,
  },
  {
    id: "pagamentos",
    filename: "indicadores_pagamentos.xlsx",
    buildWorkbook: buildWorkbookPayments,
  },
];

async function writeWorkbookToZip(zip, filename, wb) {
  const array = XLSX.write(wb, { bookType: "xlsx", type: "array" });
  zip.file(
    filename,
    new Blob([array], {
      type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
    }),
    { compression: "STORE" }
  );
}

export async function exportAllExcelsZip(
  zipName = "indicadores_projetos_em_curso_excels.zip",
  progress
) {
  const zip = new JSZip();
  const uniq = makeUniqueNamer();
  let added = 0;

  for (const entry of EXCEL_EXPORTERS || []) {
    if (!entry) continue;
    const { id, filename, buildWorkbook, buildMany } = entry;

    try {
      if (typeof buildMany === "function") {
        const many = await buildMany();
        if (Array.isArray(many) && many.length) {
          for (const f of many) {
            if (!f || !f.workbook || !f.filename) continue;
            const path = f.folder ? `${f.folder}/${f.filename}` : f.filename;
            await writeWorkbookToZip(zip, uniq(path), f.workbook);
            added++;
          }
        } else {
          zip.file(`__falhas/${id}.txt`, "buildMany devolveu lista vazia.");
        }
        continue;
      }

      if (typeof buildWorkbook === "function") {
        const wb = await buildWorkbook();
        const safeName = uniq(filename || `${id}.xlsx`);
        await writeWorkbookToZip(zip, safeName, wb);
        added++;
        continue;
      }

      zip.file(
        `__faltam_exporters/${id}.txt`,
        `Ainda não existe exporter Excel válido para a secção "${id}".`
      );
    } catch (e) {
      zip.file(
        `__falhas/${id}.txt`,
        `Falha a gerar Excel(s) de "${id}": ${e?.message || e}`
      );
    }
  }

  if (added === 0) {
    zip.file(
      "__info.txt",
      "Nenhum Excel adicionado. Verifique EXCEL_EXPORTERS e os exporters individuais."
    );
  }

  const out = await zip.generateAsync(
    { type: "blob", compression: "STORE" },
    (metadata) => {
      progress?.onZipProgress?.(metadata.percent || 0);
    }
  );
  saveAs(out, zipName);
}
